import { useEffect, useRef, useState } from 'react';

import { css } from '@compiled/react';
import { useIntl } from 'react-intl';

import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import { LoadingButton } from '@atlaskit/button';

import { useAnalyticsClient } from '@/analytics/wac-analytics/analytics-client';

import { LeftHandSidePageProjectDetails } from '../../../../onboarding-wizard/wizard/page-project-details/left-hand-side/left-hand-side';
import { StepContext, StepProps } from '../../../types';
import { Header } from '../../components/header';

import { stepMessages } from './messages';

export const SpaceDetails = <T extends StepContext>({
	context,
	setContext,
	onSubmit,
	nextStep,
}: StepProps<T>) => {
	const [isSubmitting, setIsSubmitting] = useState(false);
	const { formatMessage } = useIntl();
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const analyticsClient = useAnalyticsClient();

	/**
	 * Mirrors the project name field on every change so onSubmit can use the latest value even if
	 * the user clicks Next before React re-renders after the last keystroke (stale render `context`).
	 */
	const latestSpaceNameRef = useRef(context?.spaceName ?? '');

	useEffect(() => {
		latestSpaceNameRef.current = context?.spaceName ?? '';
	}, [context?.spaceName]);

	useEffect(() => {
		if (!analyticsClient) {
			return;
		}
		createAnalyticsEvent({
			action: 'viewed',
			actionSubject: 'screen',
			actionSubjectId: 'spaceDetailsStep',
			eventType: 'screen',
			name: 'spaceDetailsStep',
			attributes: {
				teamType: context.teamType || 'unknown',
			},
		}).fire();
	}, [createAnalyticsEvent, analyticsClient, context.teamType]);

	const handleSpaceNameChange = (spaceName: string) => {
		latestSpaceNameRef.current = spaceName;
		setContext(
			(prevContext: T | undefined) =>
				({
					...prevContext,
					spaceName,
				}) as T,
		);
	};

	const SubmitButton = (
		<LoadingButton type="submit" appearance="primary" isLoading={isSubmitting}>
			{formatMessage(stepMessages.nextBtn)}
		</LoadingButton>
	);

	return (
		<div css={containerCss}>
			<LeftHandSidePageProjectDetails
				currentProjectName={context?.spaceName ?? ''}
				defaultProjectName={context?.spaceName ?? ''}
				onProjectNameChange={handleSpaceNameChange}
				onClickNext={async () => {
					const spaceNameFromField = latestSpaceNameRef.current;
					setContext(
						(prev) =>
							({
								...prev,
								spaceName: spaceNameFromField,
							}) as T,
					);
					if (onSubmit) {
						setIsSubmitting(true);
						try {
							await onSubmit({
								context: {
									...context,
									spaceName: spaceNameFromField,
								} as T,
								setContext,
							});
						} finally {
							setIsSubmitting(false);
						}
					}

					nextStep();
				}}
				Header={Header}
				submitButton={SubmitButton}
			/>
		</div>
	);
};

const containerCss = css({
	display: 'flex',
	justifyContent: 'center',
	height: '100%',
});
